import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo } from 'react';
import { IconFormIcons, Target } from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { SideNavigationItem } from 'shared/components/ui/side-navigation-item';
import { ID } from 'shared/types/id';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './detail-page-link.style';

export type DetailPageLinkProps = {
  id: ID;
  link: string;
  onClick?: () => void;
};

export const DetailPageLink: React.FC<DetailPageLinkProps> = ({
  id,
  link,
  onClick,
}) => {
  const lifeAreas = useLocalizedLifeAreas();
  const goals = useGoals();

  const item = useMemo(
    () =>
      goals.find((goal) => goal.id === id) ??
      lifeAreas.find((lifeArea) => lifeArea.id === id),
    [goals, lifeAreas, id],
  );

  const { end, percentage } = useMemo(() => {
    if (item && 'lifeAreaId' in item) {
      const { start, current, end } = getGoalCompletion(item, {
        goals,
      });

      return {
        end,
        percentage: getCompletionPercentage({ start, current, end }),
      };
    }

    return {};
  }, [item, goals]);

  const showIcon =
    (item && 'lifeAreaId' in item) || !isNumber(percentage) || end === Infinity;

  if (!item) {
    return null;
  }

  return (
    <SideNavigationItem
      start={
        !showIcon ? (
          <Styled.CircleProgressContainer>
            <CircleProgress percentage={percentage} hidePercentage />
          </Styled.CircleProgressContainer>
        ) : null
      }
      icon={showIcon && item.iconName ? IconFormIcons[item.iconName] : Target}
      label={item.name}
      link={link}
      onClick={onClick}
    />
  );
};
